import { useCallback, useEffect, useMemo, useState } from "react";
import type { SitePermission } from "../components/SiteAccessList";
import type { CameraPermission } from "../components/CameraAccessList";
import type { EditUserFormData } from "../components/EditUserForm/EditUserForm";
import { useAccessSelection, type AccessPermission } from "./useAccessSelection";

export type UseEditUserFormStateOptions = {
  isTenantAdmin: boolean;
  sitePermissions: SitePermission[];
  cameraPermissions: CameraPermission[];
  onSubmit: (data: EditUserFormData) => void;
};

export type UseEditUserFormStateResult = {
  tenantAdmin: boolean;
  siteSelection: Map<string, "manager" | "viewer">;
  cameraSelection: Map<string, "manager" | "viewer">;
  handleTenantAdminToggle: () => void;
  handleSiteToggle: (id: string) => void;
  handleSiteRoleChange: (id: string, role: "manager" | "viewer") => void;
  handleCameraToggle: (id: string) => void;
  handleCameraRoleChange: (id: string, role: "manager" | "viewer") => void;
  handleSubmit: () => void;
};

/**
 * Manages the edit-user form state, resynchronizing from server props whenever they change.
 * Delegates selection tracking to useAccessSelection which handles reset-on-change and
 * remembers last-used roles.
 */
export function useEditUserFormState({
  isTenantAdmin,
  sitePermissions,
  cameraPermissions,
  onSubmit,
}: UseEditUserFormStateOptions): UseEditUserFormStateResult {
  const [tenantAdmin, setTenantAdmin] = useState(isTenantAdmin);

  useEffect(() => {
    setTenantAdmin(isTenantAdmin);
  }, [isTenantAdmin]);

  const normalizedSitePerms: AccessPermission[] = useMemo(
    () => sitePermissions.map((p) => ({ id: p.siteId, role: p.role })),
    [sitePermissions],
  );

  const normalizedCameraPerms: AccessPermission[] = useMemo(
    () => cameraPermissions.map((p) => ({ id: p.cameraId, role: p.role })),
    [cameraPermissions],
  );

  const {
    selected: siteSelection,
    handleToggle: handleSiteToggle,
    handleRoleChange: handleSiteRoleChange,
  } = useAccessSelection({ permissions: normalizedSitePerms });

  const {
    selected: cameraSelection,
    handleToggle: handleCameraToggle,
    handleRoleChange: handleCameraRoleChange,
  } = useAccessSelection({ permissions: normalizedCameraPerms });

  const handleTenantAdminToggle = useCallback(() => {
    setTenantAdmin((prev) => !prev);
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit({
      tenantAdmin,
      sites: siteSelection,
      cameras: cameraSelection,
    });
  }, [onSubmit, tenantAdmin, siteSelection, cameraSelection]);

  return {
    tenantAdmin,
    siteSelection,
    cameraSelection,
    handleTenantAdminToggle,
    handleSiteToggle,
    handleSiteRoleChange,
    handleCameraToggle,
    handleCameraRoleChange,
    handleSubmit,
  };
}
